import { createRoot } from 'react-dom/client';
import { BrowserRouter, Routes, Route } from 'react-router';

// Main CSS file with Tailwind and all styles
import './index.css';

import App from './App';
import { getLanguage, setLanguageToElement, validHostNames } from './utils/helper';
import { setAxiosHeaders } from './modules/auth/shared/hooks/useTokenManager';
import { registerAuthInterceptors } from './modules/auth/shared/interceptors';
import { useAppStore } from './modules/shared/store/appStore';
import { initPostHog } from './modules/shared/analytics';
import { subscribeToLogout } from './modules/auth/shared/authChannel';
import { clearClientSession } from './modules/auth/shared/performLogout';

// Import and register migrated components

declare let window: any;
// Before anything can issue a request: installs the 401 -> refresh -> retry lifecycle and the
// platform header. These used to register as a side effect of importing `commonjs/api`, which
// happened via Router -> App; registering here makes the ordering explicit rather than incidental.
registerAuthInterceptors();
setAxiosHeaders();
initPostHog(); // no-op unless window.env.POSTHOG_KEY is injected for this environment

// Logging out in one tab ends the session in all of them. Registered before React
// mounts so a tab left open on any route still reacts. The handler must not revoke
// or re-broadcast — the tab that initiated the logout already did both.
subscribeToLogout(() => {
  clearClientSession();
  window.location.replace('/');
});

/**
 * Recover a tab that was open across a deploy.
 *
 * Every chunk filename carries a content hash, so a deploy replaces all of them. A tab loaded
 * before it still holds the old module graph and asks for files that no longer exist, and each
 * lazy route then dies with "Failed to fetch dynamically imported module". Vite raises
 * `vite:preloadError` for exactly this case. Reloading fetches the new index.html and with it the
 * new chunk names.
 *
 * This matters more since the entry chunk was split: far more of the app now arrives lazily, so a
 * stale tab breaks more routes than it used to.
 *
 * Guarded so a chunk that is genuinely missing cannot loop the tab. We reload at most once every
 * RELOAD_COOLDOWN_MS; a second failure inside that window is left alone so the error surfaces
 * normally. sessionStorage is per-tab and dies with it, and every access is wrapped because it
 * throws outright when the browser blocks site data.
 */
const PRELOAD_RELOAD_KEY = 'chunk_preload_reload_at';
const RELOAD_COOLDOWN_MS = 15000;

window.addEventListener('vite:preloadError', (event: Event) => {
  let lastReloadAt = 0;
  try {
    lastReloadAt = Number(window.sessionStorage.getItem(PRELOAD_RELOAD_KEY)) || 0;
  } catch {
    // Site data blocked. Fall through and reload once; without storage we cannot rate-limit,
    // but a reload also cannot be recorded, so the browser's own failure surfaces instead.
  }
  if (lastReloadAt && Date.now() - lastReloadAt < RELOAD_COOLDOWN_MS) return;

  // Stop Vite rethrowing the error before the navigation starts.
  event.preventDefault();
  try {
    window.sessionStorage.setItem(PRELOAD_RELOAD_KEY, String(Date.now()));
  } catch {
    // ignore
  }
  window.location.reload();
});

(function () {
  const lang = getLanguage() || 'thai';
  setLanguageToElement(lang);
  useAppStore.getState().setLanguage(lang === 'english' ? 'en' : 'th');
})();

function silentErrorHandler() {
  return true;
}

if (validHostNames[window.location.hostname as keyof typeof validHostNames]) {
  // window.console.log = function () {};
  window.console.error = function () {};
  window.console.info = function () {};
  window.console.warn = function () {};
  window.console.clear = function () {};
  window.console.debug = function () {};
  window.onerror = silentErrorHandler;
}

const root = createRoot(document.getElementById('root') as HTMLElement);
root.render(
  <BrowserRouter>
    <Routes>
      <Route path="/*" element={<App />} />
    </Routes>
  </BrowserRouter>,
);
